import { useQuery } from "@tanstack/react-query";
import { useNavigate, useSearch } from "@tanstack/react-router";
import { useId, useState, type ReactElement } from "react";
import { ContentHeader } from "@cx/ui";
import type { ConsolidatedCompany, ConsolidatedDashboard } from "@cx/contracts";
import { formatDateBR, type FinancePosition } from "@cx/domain";
import { useSession } from "../../session";
import { ChartCard } from "./ChartCard";
import { CompanyTabs } from "./CompanyTabs";
import { DateInput } from "./DashboardPage";
import { count, dayMonth, money, pct, periodLabel, reportQuery } from "./labels";
import { FilterRow, ReportStatus, reportQueryOptions } from "./shared";
import { DreTable, SalesKpis } from "./widgets";

const PATH = "/meu-negocio/consolidado";

/**
 * Consolidado das empresas (T3.10, RF-UI-06): o Mensal das empresas escolhidas somado no servidor (só as que o
 * usuário vê), com o comparativo lado a lado, as vendas entre as empresas do grupo (eliminadas do total por padrão) e
 * a posição financeira de hoje. Valores sempre em R$ (o ERP só grava BRL).
 */
export function ConsolidatedPage(): ReactElement {
  const { me, companyId, api } = useSession();
  const search = useSearch({ from: PATH });
  const navigate = useNavigate({ from: PATH });
  const set = (patch: Partial<typeof search>) =>
    navigate({ search: (s) => ({ ...s, ...patch }), replace: true });
  const pair = (a?: string, b?: string) => (a && b ? [a, b] : [undefined, undefined]);
  const [from, to] = pair(search.from, search.to);
  const [compareFrom, compareTo] = pair(search.compareFrom, search.compareTo);
  const chosen = search.companies?.split(",").filter(Boolean);
  const intercompany = search.intercompany ?? "eliminate";
  const d = useQuery({
    ...reportQueryOptions,
    // A consulta não depende da empresa ativa, mas a chave segue a convenção [recurso, companyId, ...] (FE9).
    queryKey: [
      "dashboard",
      companyId,
      "consolidated",
      from,
      to,
      compareFrom,
      compareTo,
      search.companies,
      intercompany,
    ],
    queryFn: () =>
      api<ConsolidatedDashboard>(
        `/reports/consolidated${reportQuery({ from, to, compareFrom, compareTo, companies: chosen, intercompany })}`,
      ),
  });
  const r = d.data;
  const names = new Map(r?.companies.map((c) => [c.id, c.name]));

  return (
    <>
      <ContentHeader title="Consolidado" breadcrumb={["Meu Negócio", "Consolidado"]} />
      <CompanyTabs consolidated />
      <FilterRow>
        <DateInput
          id="cons-from"
          label="De"
          value={search.from ?? r?.period.from}
          onChange={(v) => set({ from: v })}
        />
        <DateInput
          id="cons-to"
          label="Até"
          value={search.to ?? r?.period.to}
          onChange={(v) => set({ to: v })}
        />
        <DateInput
          id="cons-cfrom"
          label="Comparar de"
          value={search.compareFrom ?? r?.compare.from}
          onChange={(v) => set({ compareFrom: v })}
        />
        <DateInput
          id="cons-cto"
          label="Comparar até"
          value={search.compareTo ?? r?.compare.to}
          onChange={(v) => set({ compareTo: v })}
        />
        <div className="col-12 col-md-4">
          <CompanyFilter
            companies={me.companies.map((c) => ({ id: c.id, name: c.tradeName || c.legalName }))}
            value={chosen}
            onChange={(ids) => set({ companies: ids?.join(",") })}
          />
        </div>
        <div className="col-12 col-md-auto">
          <div className="form-check">
            <input
              id="cons-ic"
              className="form-check-input"
              type="checkbox"
              checked={intercompany === "eliminate"}
              onChange={(e) => set({ intercompany: e.target.checked ? undefined : "include" })}
            />
            <label className="form-check-label" htmlFor="cons-ic">
              Eliminar operações entre as empresas
            </label>
          </div>
        </div>
        <div className="col-12 col-md-auto">
          <button
            type="button"
            className="btn btn-link"
            onClick={() => navigate({ search: {}, replace: true })}
          >
            Mês atual
          </button>
        </div>
      </FilterRow>
      <ReportStatus loading={d.isLoading} error={d.error} />
      {r && (
        <>
          <p className="text-muted small" data-testid="consolidated-scope">
            {r.companies.length === 1 ? "1 empresa" : `${r.companies.length} empresas`} · Valores em R$ (BRL)
            · Período de {periodLabel(r.period)} comparado com {periodLabel(r.compare)} ·{" "}
            {r.intercompany === "eliminate"
              ? "Sem as operações entre as empresas"
              : "Com as operações entre as empresas"}
          </p>
          <SalesKpis cards={r.total.cards} />
          {r.forecast && (
            <div className="alert alert-info d-flex flex-wrap gap-2" data-testid="forecast">
              <strong>Previsão de fechamento do grupo em {formatDateBR(r.forecast.monthEnd)}:</strong>
              <span>{money(r.forecast.projectedCents)}</span>
              <span className="text-muted">
                (entre {money(r.forecast.lowCents)} e {money(r.forecast.highCents)}, com 80% de confiança)
              </span>
            </div>
          )}
          <div className="mb-3">
            <ComparisonTable r={r} />
          </div>
          <div className="row g-3 mb-3">
            <div className="col-xl-6">
              <ChartCard
                title="Valor por dia por empresa"
                type="bar"
                stacked
                categories={r.days.map(dayMonth)}
                series={r.companies.map((c) => ({ name: c.name, data: c.daily }))}
                unit="money"
                filename="consolidado-por-empresa"
                testId="chart-companies"
              />
            </div>
            <div className="col-xl-6">
              <ChartCard
                title="Valor total do grupo por dia"
                categories={r.days.map(dayMonth)}
                series={[
                  { name: `Período (${periodLabel(r.period)})`, data: r.total.daily },
                  { name: `Comparação (${periodLabel(r.compare)})`, data: r.total.compareDaily },
                ]}
                unit="money"
                filename="consolidado-por-dia"
              />
            </div>
          </div>
          <div className="row g-3 mb-3">
            <div className="col-xl-6">
              <DreTable dre={r.total.dre} />
            </div>
            <div className="col-xl-6">
              <IntercompanyTable r={r} names={names} />
            </div>
          </div>
          <FinanceTable r={r} />
        </>
      )}
    </>
  );
}

/** Seleção de empresas (padrão: todas as que o usuário vê). */
function CompanyFilter({
  companies,
  value,
  onChange,
}: {
  companies: { id: string; name: string }[];
  value: string[] | undefined;
  onChange(ids: string[] | undefined): void;
}): ReactElement {
  const [open, setOpen] = useState(false);
  const panel = useId();
  const chosen = value ?? companies.map((c) => c.id);
  const toggle = (id: string) => {
    const next = chosen.includes(id) ? chosen.filter((x) => x !== id) : [...chosen, id];
    if (next.length) onChange(next.length === companies.length ? undefined : next);
  };
  const summary =
    value === undefined
      ? "Todas as empresas"
      : chosen.length === 1
        ? (companies.find((c) => c.id === chosen[0])?.name ?? "1 empresa")
        : `${chosen.length} empresas`;
  return (
    <div className="position-relative">
      <span className="form-label small d-block mb-2">Empresas</span>
      <button
        type="button"
        className="btn btn-outline-secondary w-100 text-start text-truncate"
        aria-expanded={open}
        aria-controls={panel}
        onClick={() => setOpen(!open)}
      >
        {summary}
      </button>
      {open && (
        <div
          id={panel}
          className="card shadow position-absolute z-3 mt-1 p-2"
          style={{ minWidth: "16rem", maxHeight: "20rem", overflowY: "auto" }}
          onKeyDown={(e) => {
            if (e.key === "Escape") setOpen(false);
          }}
        >
          <fieldset>
            <legend className="visually-hidden">Empresas do consolidado</legend>
            {companies.map((c) => (
              <div className="form-check" key={c.id}>
                <input
                  id={`${panel}-${c.id}`}
                  className="form-check-input"
                  type="checkbox"
                  checked={chosen.includes(c.id)}
                  onChange={() => toggle(c.id)}
                />
                <label className="form-check-label" htmlFor={`${panel}-${c.id}`}>
                  {c.name}
                </label>
              </div>
            ))}
          </fieldset>
          <div className="d-flex gap-2 mt-2">
            <button
              type="button"
              className="btn btn-sm btn-link px-0"
              onClick={() => {
                onChange(undefined);
                setOpen(false);
              }}
            >
              Todas
            </button>
            <button type="button" className="btn btn-sm btn-primary ms-auto" onClick={() => setOpen(false)}>
              Fechar
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

/** Comparativo lado a lado: uma linha por empresa e o total (base do consolidado). */
function ComparisonTable({ r }: { r: ConsolidatedDashboard }): ReactElement {
  const withCost = r.companies.some((c) => c.dre.costCents !== undefined);
  const row = (c: Pick<ConsolidatedCompany, "cards" | "dre">, extra: ReactElement[]) => [
    <td key="v" className="text-end">
      {money(c.cards.totalCents.current)}
    </td>,
    <td key="o" className="text-end">
      {count(c.cards.orders.current)}
    </td>,
    <td key="t" className="text-end">
      {money(c.cards.avgTicketCents.current)}
    </td>,
    <td key="var" className="text-end">
      {pct(c.cards.totalCents.variationPct4)}
    </td>,
    ...extra,
    ...(withCost
      ? [
          <td key="m" className="text-end">
            {pct(c.dre.grossMarginPct4?.current)}
          </td>,
          <td key="l" className="text-end">
            {money(c.dre.profitCents?.current)}
          </td>,
        ]
      : []),
  ];
  return (
    <section className="box card" aria-labelledby="cmp-title" data-testid="comparison">
      <div className="card-header">
        <h2 id="cmp-title" className="h6 mb-0">
          Comparativo por empresa
        </h2>
      </div>
      <div className="table-responsive">
        <table className="table table-sm align-middle mb-0">
          <thead>
            <tr>
              <th scope="col">Empresa</th>
              <th scope="col" className="text-end">
                Valor
              </th>
              <th scope="col" className="text-end">
                Pedidos
              </th>
              <th scope="col" className="text-end">
                Ticket médio
              </th>
              <th scope="col" className="text-end">
                Variação
              </th>
              <th scope="col" className="text-end">
                Participação
              </th>
              <th scope="col" className="text-end">
                Vendas ao grupo
              </th>
              {withCost && (
                <>
                  <th scope="col" className="text-end">
                    Margem bruta
                  </th>
                  <th scope="col" className="text-end">
                    Lucro estimado
                  </th>
                </>
              )}
            </tr>
          </thead>
          <tbody>
            {r.companies.map((c) => (
              <tr key={c.id} data-testid={`company-${c.id}`}>
                <th scope="row" className="fw-normal">
                  {c.name}
                </th>
                {row(c, [
                  <td key="s" className="text-end">
                    {pct(c.sharePct4)}
                  </td>,
                  <td key="ic" className="text-end text-muted">
                    {money(c.intercompany.totalCents)}
                  </td>,
                ])}
              </tr>
            ))}
          </tbody>
          <tfoot>
            <tr className="fw-semibold" data-testid="comparison-total">
              <th scope="row">Total</th>
              {row(r.total, [
                <td key="s" className="text-end">
                  {r.companies.length ? pct(1_000_000) : "—"}
                </td>,
                <td key="ic" className="text-end text-muted">
                  {r.intercompany === "eliminate" ? "Eliminadas" : "Incluídas"}
                </td>,
              ])}
            </tr>
          </tfoot>
        </table>
      </div>
      {r.companies.some((c) => c.statuses.join() !== r.companies[0]!.statuses.join()) && (
        <p className="small text-muted p-3 mb-0">
          Cada empresa usa as próprias situações que contam como venda e os próprios impostos e taxas.
        </p>
      )}
    </section>
  );
}

/** Vendas entre as empresas da consolidação (CNPJ ou raiz de CNPJ do cliente). */
function IntercompanyTable({
  r,
  names,
}: {
  r: ConsolidatedDashboard;
  names: Map<string, string>;
}): ReactElement {
  return (
    <section className="box card h-100" aria-labelledby="ic-title" data-testid="intercompany">
      <div className="card-header">
        <h2 id="ic-title" className="h6 mb-0">
          Vendas entre as empresas do grupo
        </h2>
      </div>
      {r.intercompanyPairs.length === 0 ? (
        <p className="text-muted p-3 mb-0">Nenhuma venda entre as empresas no período.</p>
      ) : (
        <div className="table-responsive">
          <table className="table table-sm align-middle mb-0">
            <thead>
              <tr>
                <th scope="col">De</th>
                <th scope="col">Para</th>
                <th scope="col" className="text-end">
                  Valor
                </th>
                <th scope="col" className="text-end">
                  Pedidos
                </th>
              </tr>
            </thead>
            <tbody>
              {r.intercompanyPairs.map((p) => (
                <tr key={`${p.fromCompanyId}-${p.toCompanyId}`}>
                  <td>{names.get(p.fromCompanyId) ?? "—"}</td>
                  <td>{names.get(p.toCompanyId) ?? "—"}</td>
                  <td className="text-end">{money(p.totalCents)}</td>
                  <td className="text-end">{count(p.orders)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <p className="small text-muted p-3 mb-0">
        {r.intercompany === "eliminate"
          ? "Estas vendas não entram no total do grupo (o cliente é outra empresa da consolidação)."
          : "Estas vendas estão somadas no total do grupo."}
      </p>
    </section>
  );
}

const FINANCE_COLUMNS: [keyof FinancePosition, string][] = [
  ["receivableOpenCents", "A receber"],
  ["receivableOverdueCents", "A receber vencido"],
  ["payableOpenCents", "A pagar"],
  ["payableOverdueCents", "A pagar vencido"],
  ["accountsBalanceCents", "Saldo em contas"],
];

/** Posição financeira de hoje por empresa; cada coluna só com a permissão do módulo dela. */
function FinanceTable({ r }: { r: ConsolidatedDashboard }): ReactElement | null {
  const cols = FINANCE_COLUMNS.filter(([k]) => r.companies.some((c) => k in c.finance));
  if (cols.length === 0) return null;
  return (
    <section className="box card mb-3" aria-labelledby="fin-title" data-testid="finance">
      <div className="card-header">
        <h2 id="fin-title" className="h6 mb-0">
          Financeiro hoje
        </h2>
      </div>
      <div className="table-responsive">
        <table className="table table-sm align-middle mb-0">
          <thead>
            <tr>
              <th scope="col">Empresa</th>
              {cols.map(([k, label]) => (
                <th key={k} scope="col" className="text-end">
                  {label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {r.companies.map((c) => (
              <tr key={c.id}>
                <th scope="row" className="fw-normal">
                  {c.name}
                </th>
                {cols.map(([k]) => (
                  <td key={k} className="text-end">
                    {k in c.finance ? money(c.finance[k]) : "—"}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
          <tfoot>
            <tr className="fw-semibold">
              <th scope="row">Total do grupo</th>
              {cols.map(([k]) => (
                <td key={k} className="text-end">
                  {k in r.total.finance ? money(r.total.finance[k]) : "—"}
                </td>
              ))}
            </tr>
          </tfoot>
        </table>
      </div>
      {r.intercompany === "eliminate" && (
        <p className="small text-muted p-3 mb-0">
          O total não soma as contas a receber e a pagar entre as empresas da consolidação.
        </p>
      )}
    </section>
  );
}
